import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import PayoutDialogShell, { labelCss, inputCss, errorCss, primaryBtn, secondaryBtn } from './PayoutDialogShell';
import { getPayoutConfig, putPayoutConfig } from '../../utils/api';
import { holdDaysValid, settingsErrorMessage, apiErrorMessage, scheduleToKeep } from '../../utils/payoutFormat';

const cardCss = { background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20 };

// Read the current values from the API's payoutConfig. The flag only counts when it is exactly true.
const toCfg = function(d) {
  return {
    frequency: d.frequency || 'manual',
    payoutDay: d.payoutDay == null ? null : d.payoutDay,
    enabled:   d.sessionPayoutEnabled === true,
    holdDays:  Number.isInteger(d.sessionPayoutHoldDays) ? d.sessionPayoutHoldDays : 2,
  };
};

// Per-session payout switch + hold days. Uses GET/PUT /api/settings/payout-config.
// Turning ON is only possible with a manual payout schedule (the API refuses otherwise), so turning it on
// sends frequency 'manual'; every other save sends the schedule exactly as it is now.
const PayoutSettingsCard = () => {
  const { user } = useAuth();
  const allowed = !!user && ['admin', 'centreAdmin'].indexOf(user.role) !== -1;
  const [phase,     setPhase]     = useState('loading');   // loading | ready | solo | error
  const [loadErr,   setLoadErr]   = useState('');
  const [cfg,       setCfg]       = useState(null);        // { frequency, payoutDay, enabled, holdDays }
  const [holdInput, setHoldInput] = useState('2');
  const [confirm,   setConfirm]   = useState(null);        // 'on' | 'off' | null
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState('');
  const [notice,    setNotice]    = useState('');

  const load = useCallback(async () => {
    setPhase('loading');
    setLoadErr('');
    try {
      const res = await getPayoutConfig();
      const next = toCfg(res.data.data || {});
      setCfg(next);
      setHoldInput(String(next.holdDays));
      setPhase('ready');
    } catch (err) {
      if (err.response && err.response.data && err.response.data.code === 'SOLO_TENANT') setPhase('solo');
      else { setLoadErr(apiErrorMessage(err, 'Could not load the payout settings.')); setPhase('error'); }
    }
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) return null;

  const save = async function(body, doneText) {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await putPayoutConfig(body);
      const next = toCfg(res.data.data || {});
      setCfg(next);
      setHoldInput(String(next.holdDays));
      setConfirm(null);
      setNotice(doneText);
    } catch (err) {
      setError(settingsErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const holdOk    = holdDaysValid(holdInput);
  const holdDirty = cfg && holdOk && Number(holdInput) !== cfg.holdDays;
  const keepSchedule = scheduleToKeep(cfg);
  const turnOn  = function() { save({ frequency: 'manual', payoutDay: null, sessionPayoutEnabled: true, sessionPayoutHoldDays: Number(holdInput) }, 'Per-session payouts are on.'); };
  const turnOff = function() { save(Object.assign({}, keepSchedule, { sessionPayoutEnabled: false }), 'Per-session payouts are off.'); };
  const saveHold = function() { save(Object.assign({}, keepSchedule, { sessionPayoutHoldDays: Number(holdInput) }), 'Hold days saved.'); };

  return (
    <div style={cardCss}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <strong style={{ fontSize: 15, color: '#1a3d2b' }}>Per-session healer payouts</strong>
        {cfg && (
          <span style={{ background: cfg.enabled ? '#d1fae5' : '#f3f4f6', color: cfg.enabled ? '#065f46' : '#4b5563', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 }}>
            {cfg.enabled ? 'On' : 'Off'}
          </span>
        )}
      </div>
      {phase === 'loading' && <div style={{ fontSize: 13, color: '#6b7280' }}>Loading…</div>}
      {phase === 'solo' && <div style={{ fontSize: 13, color: '#6b7280' }}>Per-session payouts aren't available for solo practices.</div>}
      {phase === 'error' && (
        <div style={{ fontSize: 13, color: '#991b1b' }}>
          {loadErr} <button onClick={load} style={Object.assign({}, secondaryBtn, { padding: '3px 10px', fontSize: 12 })}>Retry</button>
        </div>
      )}
      {phase === 'ready' && cfg && (
        <div>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151' }}>
            When on, each completed session creates a payout line below. LifeChiAI does not send money — you pay healers yourself and record it here.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={labelCss} htmlFor="hold-days">Hold days (0–30)</label>
              <input id="hold-days" type="number" min="0" max="30" step="1" value={holdInput} disabled={busy}
                onChange={function(e) { setHoldInput(e.target.value); setError(''); }}
                style={Object.assign({}, inputCss, { width: 110 })} />
            </div>
            {holdDirty && (
              <button style={secondaryBtn} disabled={busy} onClick={saveHold}>Save hold days</button>
            )}
            {cfg.enabled
              ? <button style={secondaryBtn} disabled={busy} onClick={function() { setError(''); setConfirm('off'); }}>Turn off</button>
              : <button style={primaryBtn(!holdOk || busy)} disabled={!holdOk || busy} onClick={function() { setError(''); setConfirm('on'); }}>Turn on</button>}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>Hold days apply to sessions logged after the change.</div>
          {!holdOk && <div style={Object.assign({}, errorCss, { marginTop: 8 })}>Hold days must be a whole number from 0 to 30.</div>}
          {!confirm && error && <div style={errorCss}>{error}</div>}
          {notice && <div role="status" style={{ marginTop: 10, fontSize: 13, color: '#065f46' }}>{notice}</div>}
        </div>
      )}

      {confirm === 'on' && cfg && (
        <PayoutDialogShell title="Turn on per-session payouts?" onClose={function() { setConfirm(null); }} busy={busy}>
          <p style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>
            Per-session healer payouts will start for new sessions. LifeChiAI does not send money. You pay healers yourself and record it here.
          </p>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: '#374151' }}>
            While this is on, automatic per-session invoices and automatic healer payouts are switched off for new sessions. Hold window: {Number(holdInput)} {Number(holdInput) === 1 ? 'day' : 'days'}.
          </p>
          {cfg.frequency !== 'manual' && (
            <p style={{ margin: '0 0 10px', fontSize: 13, color: '#92400e' }}>
              This also sets your payout schedule to manual (it is currently {cfg.frequency}).
            </p>
          )}
          {error && <div style={errorCss}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={secondaryBtn} onClick={function() { setConfirm(null); }} disabled={busy}>Cancel</button>
            <button style={primaryBtn(busy)} onClick={turnOn} disabled={busy}>{busy ? 'Saving…' : 'Turn on'}</button>
          </div>
        </PayoutDialogShell>
      )}
      {confirm === 'off' && (
        <PayoutDialogShell title="Turn off per-session payouts?" onClose={function() { setConfirm(null); }} busy={busy}>
          <p style={{ margin: '0 0 10px', fontSize: 14, color: '#374151' }}>
            Per-session payouts will stop for new sessions. Lines already created stay on this page and can still be released, cancelled or marked paid.
          </p>
          {error && <div style={errorCss}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={secondaryBtn} onClick={function() { setConfirm(null); }} disabled={busy}>Cancel</button>
            <button style={primaryBtn(busy)} onClick={turnOff} disabled={busy}>{busy ? 'Saving…' : 'Turn off'}</button>
          </div>
        </PayoutDialogShell>
      )}
    </div>
  );
};

export default PayoutSettingsCard;
